import { useState } from 'react';
import { useNavigate, Link, useLocation, Navigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Palmtree } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Button from '../components/Button';
import { useAuth, type User } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { LAST_TURISTA_EMAIL_KEY } from '../lib/persistSession';
import './Login.css';

const GOOGLE_OAUTH_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

/** Botão de login com Google — só é renderizado quando OAuth está configurado e dentro de GoogleOAuthProvider */
function GoogleLoginButton({
    onSuccess,
    onError,
    disabled,
}: {
    onSuccess: (tokenResponse: any) => void;
    onError: () => void;
    disabled: boolean;
}) {
    const googleLogin = useGoogleLogin({
        onSuccess: onSuccess,
        onError: onError,
    });
    return (
        <>
            <div className="login-divider">
                <span>ou</span>
            </div>
            <div className="social-login">
                <button
                    type="button"
                    className="social-btn google"
                    onClick={() => googleLogin()}
                    disabled={disabled}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continuar com Google
                </button>
            </div>
        </>
    );
}

const loginSchema = z.object({
    email: z.string().email('Digite um email válido'),
    password: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres'),
});

type LoginFormInputs = z.infer<typeof loginSchema>;

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated } = useAuth();
    const toast = useToast();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormInputs>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: typeof localStorage !== 'undefined' ? localStorage.getItem(LAST_TURISTA_EMAIL_KEY) ?? '' : '',
            password: '',
        },
    });

    if (isAuthenticated) {
        return <Navigate to={from} replace />;
    }

    const onSubmit = async (data: LoginFormInputs) => {
        setLoading(true);

        try {
            const { data: user, error } = await supabase.rpc('authenticate_user', {
                p_email: data.email,
                p_password: data.password,
            });

            if (error) throw new Error(error.message);
            const row = Array.isArray(user) ? user[0] : user;
            if (!row || typeof row !== 'object') throw new Error('Email ou senha incorretos');

            localStorage.setItem(LAST_TURISTA_EMAIL_KEY, data.email);
            login(row as User);
            navigate('/');
        } catch (err: any) {
            toast.error(err.message || 'Email ou senha incorretos');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (tokenResponse: any) => {
        setLoading(true);
        try {
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
            });
            const userInfo = await userInfoResponse.json();

            // Check if user exists, create if not
            const { data: existing } = await supabase
                .from('User')
                .select('*')
                .eq('email', userInfo.email)
                .single();

            let userData;
            if (existing) {
                userData = existing;
            } else {
                const { data: newUser, error } = await supabase
                    .from('User')
                    .insert({
                        name: userInfo.name,
                        email: userInfo.email,
                        avatar: userInfo.picture,
                    })
                    .select()
                    .single();
                if (error) throw new Error(error.message);
                userData = newUser;
            }

            localStorage.setItem(LAST_TURISTA_EMAIL_KEY, userInfo.email);
            login(userData);
            navigate('/');
        } catch (err: any) {
            toast.error(err.message || 'Erro ao fazer login com Google');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-background">
                <div className="bg-shape shape-1"></div>
                <div className="bg-shape shape-2"></div>
                <div className="bg-shape shape-3"></div>
            </div>

            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-icon">
                            <Palmtree size={32} />
                        </div>
                        <h1>Facilita Pass+</h1>
                        <p>Bem-vindo de volta! Faça login para continuar.</p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="login-form">
                        <div className="input-group">
                            <div className="input-icon">
                                <Mail size={20} />
                            </div>
                            <input
                                type="email"
                                placeholder="Email"
                                {...register('email')}
                                className={errors.email ? 'error' : ''}
                            />
                        </div>
                        {errors.email && <span className="field-error">{errors.email.message}</span>}

                        <div className="input-group">
                            <div className="input-icon">
                                <Lock size={20} />
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Senha"
                                {...register('password')}
                                className={errors.password ? 'error' : ''}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors.password && <span className="field-error">{errors.password.message}</span>}

                        <div className="forgot-password">
                            <Link to="/forgot-password">Esqueceu a senha?</Link>
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            fullWidth
                            disabled={loading}
                            isLoading={loading}
                            className="login-btn"
                        >
                            {loading ? 'Entrando...' : 'Entrar'}
                        </Button>
                    </form>

                    {GOOGLE_OAUTH_ENABLED && (
                        <GoogleLoginButton
                            onSuccess={handleGoogleSuccess}
                            onError={() => toast.error('Erro ao conectar com Google')}
                            disabled={loading}
                        />
                    )}

                    <div className="login-footer">
                        <p>Não tem uma conta? <Link to="/register">Cadastre-se</Link></p>
                        <p className="partner-link">É dono de um estabelecimento? <Link to="/merchant-login">Acesse como Parceiro</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
